import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '../Image';
import WhatsIncluded, { WhatsIncludedItem } from './WhatsIncluded';
import * as events from '../../lib/analytics/events';
import styles from './CartItemV2.module.scss';
import * as types from '../../types';
import useFreeTrialShippingPrice from '../../hooks/useFreeTrialShippingPrice';
import FreeTrialLearnMoreModal from '../FreeTrialLearnMoreModal';

export interface CartItemV2Props {
  cartItemId: types.CartItemID;
  productImageUrl: string;
  freeTrialDays: number;
  removeCartItem?: () => void;
  defaultExpanded?: boolean;
  includedItems?: WhatsIncludedItem[];
  subscriptionPrice?: string;
}

/**
 * V2 cart item component for free trial bundles in Canada.
 * Shows a streamlined view with "What's included?" accordion.
 */
export default function CartItemV2({
  cartItemId,
  productImageUrl,
  freeTrialDays,
  removeCartItem,
  defaultExpanded = true,
  includedItems,
  subscriptionPrice,
}: CartItemV2Props) {
  const { t } = useTranslation();

  const freeTrialMonths = Math.round(freeTrialDays / 30);
  const shippingPrice = useFreeTrialShippingPrice();

  return (
    <div className={styles.cartItemV2} data-cart-item-id={cartItemId}>
      <div className={styles.header}>
        <div className={styles.imageContainer}>
          <Image
            className={styles.productImage}
            image={{ type: 'image', url: productImageUrl }}
            alt={t('cart_item_v2.trial_membership')}
          />
        </div>

        <div className={styles.headerContent}>
          <div className={styles.titleRow}>
            <div className={styles.titleGroup}>
              <h3 className={styles.title}>{t('cart_item_v2.trial_membership')}</h3>
              <p className={styles.subtitle}>{t('cart_item_v2.free_trial_subtitle', { days: freeTrialMonths })}</p>
            </div>

            <div className={styles.priceGroup}>
              <span className={styles.priceMain}>{t('cart_item_v2.price_free')}</span>
            </div>
          </div>

          {shippingPrice && (
            <div className={styles.shippingRow}>
              <span>{t('cart_item_v2.shipping')}</span>
              <span>{shippingPrice}</span>
            </div>
          )}

          {removeCartItem && (
            <button type="button" className={styles.removeLink} onClick={removeCartItem}>
              {t('cart_item_v2.remove')}
            </button>
          )}

          <p className={styles.disclaimer}>
            {t('cart_item_v2.trial_disclaimer')}{' '}
            {shippingPrice && subscriptionPrice ? (
              <FreeTrialLearnMoreModal
                trigger={<span className={styles.learnMoreLink}>{t('cart_item_v2.learn_more')}</span>}
                onOpen={() => events.freeTrialLearnMoreClickedFromCart()}
                shippingPrice={shippingPrice}
                subscriptionPrice={subscriptionPrice}
              />
            ) : (
              <a
                href="https://support.tryfi.com/hc/en-us/articles/48272655778579-Free-Trial-Program-Canada-Only"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.learnMoreLink}
                onClick={() => events.freeTrialLearnMoreClickedFromCart()}
              >
                {t('cart_item_v2.learn_more')}
              </a>
            )}
          </p>
        </div>
      </div>

      {includedItems && includedItems.length > 0 && (
        <WhatsIncluded items={includedItems} defaultExpanded={defaultExpanded} />
      )}
    </div>
  );
}
